import React, { useEffect, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import LoadingSpinner from '../common/LoadingSpinner';
import { ContractPdfError, fetchContractPdf } from '../../services/contractService';
import type { ContractPdfErrorKind } from '../../services/contractService';
import type { ContractSummary } from '../../services/types';

/** Props der ContractPdfViewer-Komponente. */
export interface ContractPdfViewerProps {
  /** Vertrag, dessen PDF angezeigt wird (steuert auch den Titel). */
  readonly contract: ContractSummary;
  /** Schliesst die Ansicht (Elternkomponente entfernt die Komponente). */
  readonly onClose: () => void;
}

/** Meldung fuer 'not-found' (HTTP 404: kein PDF vorhanden). */
const NOT_FOUND_MESSAGE = 'Für diesen Vertrag ist kein PDF verfügbar.';

/**
 * Fallback-Meldung fuer 'incomplete' (HTTP 422 der Vorschau: Vertrag
 * unvollstaendig). Der Service liefert i. d. R. bereits eine konkretere
 * Meldung mit den fehlenden Feldern (siehe ContractPdfError.message);
 * dieser Text greift nur, falls der Fehler ausnahmsweise ohne Message
 * ankommt.
 */
const INCOMPLETE_FALLBACK_MESSAGE =
  'Der Vertrag ist unvollständig und kann deshalb noch nicht als Vorschau angezeigt werden.';

/** Meldung fuer 'unavailable' (HTTP 502/503/504: DocStruct nicht erreichbar). */
const UNAVAILABLE_MESSAGE = 'Der PDF-Dienst ist derzeit nicht erreichbar.';

/** Meldung fuer 'unknown' (Netzwerkfehler, unerwartete Antwort). */
const ERROR_MESSAGE = 'Das PDF konnte nicht geladen werden.';

/** Hoehe der PDF-Anzeige (Modal fuellt den Grossteil des Viewports). */
const VIEWER_HEIGHT = '70vh';

/** Ladeergebnis des PDF-Abrufs. */
interface PdfState {
  /** Blob-URL des geladenen PDFs oder null. */
  readonly blobUrl: string | null;
  /** Fehlerursache oder null. */
  readonly errorKind: ContractPdfErrorKind | null;
  /**
   * Konkrete Fehlermeldung des Service (z. B. mit den fehlenden Feldern
   * bei 'incomplete') oder null, wenn die statische Meldung der Ursache
   * ausreicht.
   */
  readonly errorMessage: string | null;
}

/** Startzustand: laedt (weder URL noch Fehler). */
const INITIAL_STATE: PdfState = { blobUrl: null, errorKind: null, errorMessage: null };

/** Ersetzt Zeichen, die in Dateinamen Probleme machen. */
function sanitizeFileNamePart(value: string): string {
  return value.replace(/[^\w.-]+/g, '_');
}

/**
 * Modale PDF-Ansicht eines Mietvertrags (Bootstrap-Modal, size lg).
 *
 * Das PDF wird ueber den Backend-Proxy als Blob geladen und per Blob-URL
 * in einem iframe angezeigt — ein direkter iframe-src auf den Endpunkt
 * wuerde ohne Authorization-Header laufen (das JWT haengt am
 * apiClient-Interceptor, nicht am Browser-Request des iframes).
 *
 * Die Blob-URL wird im Cleanup des Effekts freigegeben
 * (URL.revokeObjectURL); sonst bliebe das PDF bis zum Neuladen der Seite
 * im Speicher.
 *
 * Fehler werden still behandelt (KEIN console.error) und nach Ursache
 * unterschieden: kein PDF vorhanden (404), PDF-Dienst nicht erreichbar
 * (502/503/504) oder unbekannter Fehler.
 *
 * Hinweis Mobile (Capacitor): In der nativen WebView kann ein
 * blob:-iframe je nach Plattform keinen PDF-Viewer starten. Ein eigener
 * Capacitor-Pfad (Datei speichern + Systemviewer) ist bewusst NICHT Teil
 * dieser Iteration; der iframe-Ansatz bleibt der Web-Standardfall.
 */
function ContractPdfViewer({ contract, onClose }: ContractPdfViewerProps): React.JSX.Element {
  const [state, setState] = useState<PdfState>(INITIAL_STATE);
  const contractId = contract.id;
  const pdfAvailable = contract.pdfAvailable;

  useEffect(() => {
    const controller = new AbortController();
    let objectUrl: string | null = null;
    setState(INITIAL_STATE);

    fetchContractPdf(contractId, pdfAvailable, controller.signal)
      .then((blob) => {
        if (controller.signal.aborted) {
          // Abbruch (Schliessen/Vertragswechsel): keine URL erzeugen
          return;
        }
        objectUrl = URL.createObjectURL(blob);
        setState({ blobUrl: objectUrl, errorKind: null, errorMessage: null });
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) {
          return;
        }
        if (error instanceof ContractPdfError) {
          setState({ blobUrl: null, errorKind: error.kind, errorMessage: error.message });
        } else {
          setState({ blobUrl: null, errorKind: 'unknown', errorMessage: null });
        }
      });

    return () => {
      controller.abort();
      if (objectUrl !== null) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [contractId, pdfAvailable]);

  const title = contract.contractNumber ?? 'ohne Vertragsnummer';
  const fileName = `Mietvertrag-${sanitizeFileNamePart(contract.contractNumber ?? contract.id)}.pdf`;
  const loading = state.blobUrl === null && state.errorKind === null;

  return (
    <Modal
      id="contract-pdf-modal"
      show
      size="lg"
      /*
        Auf schmalen Geraeten (< 768 px) fuellt die Ansicht den Bildschirm:
        Ein PDF in einem zentrierten Modal mit Aussenabstand waere dort
        kaum lesbar.
      */
      fullscreen="md-down"
      centered
      onHide={onClose}
      aria-labelledby="contract-pdf-title"
    >
      <Modal.Header id="contract-pdf-header">
        <Modal.Title id="contract-pdf-title" className="h6 mb-0">
          {`Mietvertrag ${title}`}
        </Modal.Title>
        {/*
          ms-auto: Der Schliessen-Button gehoert an den rechten Rand der
          Kopfzeile, unabhaengig von der Breite des Titels.
        */}
        <Button
          id="contract-pdf-close"
          variant="outline-secondary"
          size="sm"
          className="ms-auto"
          title="Schließen"
          onClick={onClose}
        >
          <i id="contract-pdf-close-icon" className="bi bi-x-lg"></i>
        </Button>
      </Modal.Header>

      <Modal.Body id="contract-pdf-body" className="p-0">
        {loading && (
          <div
            id="contract-pdf-loading-container"
            className="d-flex align-items-center justify-content-center"
            style={{ height: VIEWER_HEIGHT }}
          >
            <LoadingSpinner id="contract-pdf-loading" />
          </div>
        )}

        {state.errorKind === 'not-found' && (
          <Alert id="contract-pdf-not-found" variant="warning" className="m-3">
            {NOT_FOUND_MESSAGE}
          </Alert>
        )}

        {/*
          variant="info" statt "warning"/"danger": Ein unvollstaendiger
          Entwurf ist ein normaler fachlicher Zwischenzustand, kein Fehler
          im technischen Sinn — die Meldung soll nicht wie ein Systemfehler
          wirken.
        */}
        {state.errorKind === 'incomplete' && (
          <Alert id="contract-pdf-incomplete" variant="info" className="m-3">
            {state.errorMessage ?? INCOMPLETE_FALLBACK_MESSAGE}
          </Alert>
        )}

        {state.errorKind === 'unavailable' && (
          <Alert id="contract-pdf-unavailable" variant="danger" className="m-3">
            {UNAVAILABLE_MESSAGE}
          </Alert>
        )}

        {state.errorKind === 'unknown' && (
          <Alert id="contract-pdf-error" variant="danger" className="m-3">
            {ERROR_MESSAGE}
          </Alert>
        )}

        {/*
          BEWUSST OHNE sandbox-Attribut.

          Getestet (Chromium 1234 via Playwright, headless UND headed, mit
          einem echten Blob-PDF): Sobald das iframe ein sandbox-Attribut
          traegt, rendert Chromium das PDF NICHT mehr — der interne
          PDF-Viewer ist ein Plugin, und Plugins sind in sandboxed frames
          generell gesperrt. Geprueft wurden sandbox="",
          "allow-same-origin", "allow-scripts", "allow-downloads",
          "allow-same-origin allow-downloads", "allow-scripts
          allow-same-origin" sowie ALLE Tokens gemeinsam: In jedem Fall
          blieb das iframe leer (ein Token "allow-plugins" existiert
          nicht mehr). Ohne Attribut wird das PDF zuverlaessig angezeigt.

          Der Schutz haengt deshalb am Service: fetchContractPdf laesst
          nur Bloebe mit dem MIME-Typ application/pdf durch und erzeugt
          typlose Antworten explizit als application/pdf neu (siehe
          ensurePdfBlob in contractService). Damit kann die blob:-URL —
          die den Origin dieses Dokuments erbt — kein HTML und kein
          JavaScript ausfuehren und das JWT aus der sessionStorage nicht
          erreichen.
        */}
        {state.blobUrl !== null && (
          <iframe
            id="contract-pdf-frame"
            title={`Mietvertrag ${title}`}
            src={state.blobUrl}
            style={{ width: '100%', height: VIEWER_HEIGHT, border: 'none' }}
          ></iframe>
        )}
      </Modal.Body>

      {/*
        Fusszeile nur mit geladenem PDF: Ohne Download-Ziel bliebe sonst
        eine leere Leiste unter der Fehlermeldung stehen.
      */}
      {state.blobUrl !== null && (
        <Modal.Footer id="contract-pdf-footer">
          <a
            id="contract-pdf-download"
            className="btn btn-primary btn-sm"
            href={state.blobUrl}
            download={fileName}
          >
            <i id="contract-pdf-download-icon" className="bi bi-download me-1"></i>
            Herunterladen
          </a>
        </Modal.Footer>
      )}
    </Modal>
  );
}

export default ContractPdfViewer;
